import React, { useRef, useState, type JSX } from "react";

import { assertNever } from "@impero-com/common/utils/obj";
import * as Sentry from "@sentry/react";

import { SavedBlob } from "api/generated/model";

import { FileUploader, SetFilesCallback } from "./fileUploader";
import { DeleteBlobAction } from "./queries/useDeleteBlobMutation";
import { FileUploaded, FileUploadResult, UploaderFile } from "./types";
import { savedBlobToUploaderFile } from "./utils";

interface SimpleFileUploaderProps {
    savedBlobs?: SavedBlob[];
    onFileUploadPromise: (promise: Promise<FileUploadResult>) => void;
    onFileDeleted: (id: string) => void;
    deleteAction: DeleteBlobAction;
    readOnly?: boolean;
    uploaderRef?: React.RefCallback<HTMLElement>;
}

type FileUploadPromiseResolve = (result: FileUploadResult) => void;

/**
 * a FileUploader wrapper that holds all of its internal state (file uploading) and allows you to
 * work with SavedBlob and sensible callbacks
 *
 */
export function SimpleFileUploader({
    savedBlobs,
    onFileUploadPromise,
    onFileDeleted,
    readOnly,
    uploaderRef,
}: SimpleFileUploaderProps): JSX.Element {
    const disabled = readOnly ?? false;

    const blobs = savedBlobs ?? [];
    const [files, setFiles] = useState(() =>
        blobs.map((blob) => savedBlobToUploaderFile(blob))
    );
    const uploadPromisesRef = useRef(
        new Map<string, FileUploadPromiseResolve>()
    );
    const filesRef = useRef(files);

    const updateFiles = (newFiles: UploaderFile[]) => {
        filesRef.current = newFiles;
        setFiles(newFiles);
    };

    const onFileUpdate: SetFilesCallback = (getNewFiles) => {
        const newFiles = getNewFiles(filesRef.current);

        // Compute delta between old and new state
        const before = extractFileMap(filesRef.current);
        const after = extractFileMap(newFiles);
        const events = compareFileMaps(before, after);

        // Triggers the relevant callbacks
        updateFiles(newFiles);
        events.forEach((event) => {
            switch (event.type) {
                case "ADDED": {
                    const maybeResolver = uploadPromisesRef.current.get(
                        event.file.keyId
                    );
                    if (maybeResolver === undefined) {
                        throw new Error(
                            "Could not find a resolver for the file"
                        );
                    }
                    maybeResolver({ kind: "Uploaded", file: event.file });
                    uploadPromisesRef.current.delete(event.file.keyId);
                    break;
                }
                case "ERRORED": {
                    const maybeResolver = uploadPromisesRef.current.get(
                        event.keyId
                    );
                    if (maybeResolver === undefined) {
                        throw new Error(
                            "Could not find a resolver for the file"
                        );
                    }
                    maybeResolver({ kind: "Errored", keyId: event.keyId });
                    uploadPromisesRef.current.delete(event.keyId);
                    break;
                }
                case "UPLOAD_START":
                    const promise = new Promise<FileUploadResult>(
                        (resolve, _reject) => {
                            uploadPromisesRef.current.set(event.keyId, resolve);
                        }
                    );
                    onFileUploadPromise(promise);
                    break;
                case "REMOVED":
                    onFileDeleted(event.blobId);
                    break;
                default:
                    assertNever(event);
            }
        });
    };

    return (
        <FileUploader
            value={files}
            uploaderRef={uploaderRef}
            setValue={onFileUpdate}
            deleteAction={{ kind: "DO_NOT_DELETE_BLOB" }}
            disabled={disabled}
        />
    );
}

function extractFileMap(files: UploaderFile[]): Map<string, UploaderFile> {
    const map = new Map();
    files.forEach((file) => {
        map.set(file.keyId, file);
    });
    return map;
}

type FileUploaderEvent =
    | { type: "ADDED"; file: FileUploaded }
    | { type: "REMOVED"; blobId: string }
    | { type: "ERRORED"; keyId: string }
    | { type: "UPLOAD_START"; keyId: string };

function compareFileMaps(
    before: Map<string, UploaderFile>,
    after: Map<string, UploaderFile>
): FileUploaderEvent[] {
    const events: FileUploaderEvent[] = [];
    after.forEach((fileAfter, keyId) => {
        const beforeValue = before.get(keyId);
        switch (fileAfter.status) {
            case "success":
                switch (beforeValue?.status) {
                    case undefined:
                    case "done":
                    case "removed":
                    case "error":
                    case "missing":
                        const err = `Wrong status ${beforeValue?.status} for beforeValue.status`;
                        console.error(err);
                        Sentry.captureException(err);
                        return;
                    case "success":
                        // Same file as before. Nothing new.
                        return;
                    case "uploading":
                        const blob: SavedBlob = {
                            id: fileAfter.id,
                            fileName: fileAfter.fileName,
                            fileSize: fileAfter.fileSize,
                            fileType: fileAfter.fileType,
                            lastModified: fileAfter.lastModified,
                        };
                        events.push({
                            type: "ADDED",
                            file: { blob, keyId: fileAfter.keyId },
                        });
                        return;
                    default:
                        return assertNever(beforeValue);
                }
            case "removed":
                switch (beforeValue?.status) {
                    case undefined:
                    case "uploading":
                    case "error":
                    case "missing":
                        const err = `Wrong status ${beforeValue?.status} for beforeValue.status`;
                        console.error(err);
                        Sentry.captureException(err);
                        return;
                    case "removed":
                        // Same file as before. Nothing new.
                        return;
                    case "done":
                    case "success":
                        events.push({
                            type: "REMOVED",
                            blobId: beforeValue.id,
                        });
                        return;
                    default:
                        return assertNever(beforeValue);
                }
            case "error":
                switch (beforeValue?.status) {
                    case undefined:
                    case "done":
                    case "success":
                    case "removed":
                    case "missing":
                        const err = `Wrong status ${beforeValue?.status} for beforeValue.status`;
                        console.error(err);
                        Sentry.captureException(err);
                        return;
                    case "error":
                        // Same file as before. Nothing new.
                        return;
                    case "uploading":
                        events.push({
                            type: "ERRORED",
                            keyId: beforeValue.keyId,
                        });
                        return;
                    default:
                        return assertNever(beforeValue);
                }
            case "uploading":
                switch (beforeValue?.status) {
                    case "removed":
                    case "error":
                    case "missing":
                    case "success":
                    case "done":
                        const err = `Wrong status ${beforeValue?.status} for beforeValue.status`;
                        console.error(err);
                        Sentry.captureException(err);
                        return;
                    case undefined:
                        events.push({
                            type: "UPLOAD_START",
                            keyId: fileAfter.keyId,
                        });
                        return;
                    case "uploading":
                        // Same file as before. Nothing new.
                        return;
                    default:
                        return assertNever(beforeValue);
                }
            case "done":
                switch (beforeValue?.status) {
                    case "success":
                    case "error":
                    case "missing":
                    case "uploading":
                    case "removed":
                        const err = `Wrong status ${beforeValue?.status} for beforeValue.status`;
                        console.error(err);
                        Sentry.captureException(err);
                        return;
                    case "done":
                        // Same file as before. Nothing new.
                        return;
                    case undefined:
                        // The file was created with status "Done". Nothing new.
                        return;
                    default:
                        return assertNever(beforeValue);
                }
            case "missing":
                throw new Error(`Invalid status ${fileAfter.status}`);
            default:
                return assertNever(fileAfter);
        }
    });
    return events;
}
